// src/components/NavBar.tsx
import React from 'react';
import {
    IconButton,
    Avatar,
    Menu,
    MenuItem,
    Box,
    Typography,
    Fade,
} from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ukFlag from '../Images/GB.svg';
import frFlag from '../Images/FR.svg';
import './Navbar.css';

type NavBarProps = {
    mode: 'light' | 'dark';
    toggleTheme: () => void;
    language: 'en' | 'fr';
    setLanguage: (lang: 'en' | 'fr') => void;
};

type LanguageOption = {
    code: 'en' | 'fr';
    label: string;
    flag: string;
};

const LANGUAGES: LanguageOption[] = [
    { code: 'en', label: 'English', flag: ukFlag },
    { code: 'fr', label: 'Français', flag: frFlag },
];

const NAV_ITEMS: { en: string[]; fr: string[] } = {
    en: ['About me', 'Education / Experience', 'Skills', 'Qualities', 'Projects'],
    fr: ['A propos de moi', 'Cursus / Expérience professionnelle', 'Compétences', 'Qualités', 'Projets'],
};

const itemToId: Record<string, string> = {
    'A propos de moi': 'apropos',
    'Cursus / Expérience professionnelle': 'cursus',
    'Compétences': 'competences',
    'Qualités': 'qualites',
    'Projets': 'projets',
};

const NavBar: React.FC<NavBarProps> = ({ mode, toggleTheme, language, setLanguage }) => {
    const navItems = NAV_ITEMS[language];
    const [activeNav, setActiveNav] = React.useState<string>(navItems[0]);

    const currentLang = LANGUAGES.find(lang => lang.code === language) || LANGUAGES[0];

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelectLanguage = (lang: LanguageOption) => {
        setLanguage(lang.code);
        handleCloseMenu();
    };

    const handleNavClick = (item: string) => {
        setActiveNav(item);
        const sectionId = itemToId[item] || item.replace(/\s+/g, '').toLowerCase();
        const sectionEl = document.getElementById(sectionId);
        if (sectionEl) {
            sectionEl.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <nav className="navbar">
            <ul className="nav-links">
                {navItems.map(item => (
                    <li
                        key={item}
                        className={activeNav === item ? 'active' : ''}
                        onClick={() => handleNavClick(item)}
                    >
                        {item}
                    </li>
                ))}
            </ul>

            <div className="navbar-right">
                <IconButton onClick={handleOpenMenu} style={{ marginLeft: '12px', marginRight: '10px' }}>
                    <Fade in={true}>
                        <Avatar
                            variant="rounded"
                            src={currentLang.flag}
                            alt={currentLang.label}
                            sx={{
                                width: 32,
                                height: 24,
                                img: { objectFit: 'contain' },
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'scale(1.1)' },
                                border: 'none',
                            }}
                        />
                    </Fade>
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    TransitionComponent={Fade}
                    transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            overflow: 'hidden',
                        },
                    }}
                >
                    {LANGUAGES.map(lang => (
                        <MenuItem key={lang.code} onClick={() => handleSelectLanguage(lang)}>
                            <Box display="flex" alignItems="center">
                                <Avatar
                                    variant="rounded"
                                    src={lang.flag}
                                    alt={lang.label}
                                    sx={{
                                        width: 20,
                                        height: 15,
                                        mr: 1,
                                        img: { objectFit: 'contain' },
                                    }}
                                />
                                <Typography>{lang.label}</Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
                <IconButton onClick={toggleTheme} style={{ marginRight: '10px' }}>
                    {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon style={{ color: '#ffcc00' }} />}
                </IconButton>
            </div>
        </nav>
    );
};

export default NavBar;
